/*
PURPOSE: Editorial "How It Works" section for the home page — the personalise pitch,
the any-artists tooltip, the mobile creator, and the start-your-coral CTA.
USE CASES:
- Third section of the home page, after the value proposition.
DOMAIN BOUNDARIES:
- Thin composition of editorial primitives + verbatim
  useCopy().publicPages.home.howItWorks / cta copy. No fabricated strings.
- The "any artists" tooltip behaviour (split + react-tooltip) is preserved
  verbatim from legacy Home, including the anyArtistsTooltip HTML.
- The mobile-only creator mirrors legacy Home (the desktop creator lives in
  the hero). The CTA scrolls to the hero creator via the passed callback.
- No imports from design-sandbox/.
*/

import React from 'react';
import { Tooltip } from 'react-tooltip';
import { useCopy } from '../../../hooks/useCopy';
import { EditorialSection, Eyebrow, DisplayHeading, BodyProse, CtaPill } from '../../../components/editorial';
import EditorialCreatorCard from './EditorialCreatorCard';

interface HowItWorksProps {
  onPersonalise: () => void;
}

const HowItWorks: React.FC<HowItWorksProps> = ({ onPersonalise }) => {
  const copy = useCopy();
  const { sectionTitle, personaliseTitle, whatACoralIs, commonsAndEcosystem, anyArtistsTooltip } =
    copy.publicPages.home.howItWorks;

  const [beforeAnyArtists, afterAnyArtists] = personaliseTitle.split('any artists');

  // Italicised where the sentence defines it, and only there. Degrades to plain prose if a copy
  // edit drops the word, rather than slicing the paragraph at index -1.
  const [beforeCoral, afterCoral] = whatACoralIs.split('coral');

  return (
    <EditorialSection background="ivory" id="how-it-works">
      <Eyebrow>{sectionTitle}</Eyebrow>

      <DisplayHeading as="h2" scale="heading" className="mt-8">
        {beforeAnyArtists}
        <span
          data-tooltip-id="any-artists"
          data-tooltip-html={anyArtistsTooltip}
          className="text-editorial-accent underline decoration-dotted underline-offset-4 cursor-help"
        >
          any artists
        </span>
        {afterAnyArtists}
      </DisplayHeading>

      <BodyProse className="mt-8">
        <p>
          {afterCoral === undefined ? (
            whatACoralIs
          ) : (
            <>
              {beforeCoral}
              <em>coral</em>
              {afterCoral}
            </>
          )}
        </p>
        <p>{commonsAndEcosystem}</p>
      </BodyProse>

      <Tooltip id="any-artists" className="z-50" />

      {/* Mobile-only creator — the desktop creator lives in the hero. */}
      <div className="block md:hidden mt-12">
        <EditorialCreatorCard />
      </div>

      <div className="flex justify-center mt-16">
        <CtaPill onClick={onPersonalise}>{copy.publicPages.home.cta.personalise}</CtaPill>
      </div>
    </EditorialSection>
  );
};

export default HowItWorks;
